import { Donation, DonationsResourceMeta } from "../../types"
import { css } from "@emotion/react"
import { Fund } from "source/giving/donate/form/types"
import DonationDetails from "./DonationDetails"
import BlankState from "./BlankState"
import { formattedCents } from "source/giving/utils/money"
import pluralize from "source/shared/pluralize"
import { AlertMessage, Loading } from "source/shared/components"
import { useSuspenseInfiniteQuery } from "@tanstack/react-query"
import { fetchDonations } from "../../queries"
import InfiniteScroll from "react-infinite-scroller"
import { useDonationHistoryContext } from "../../hooks/useDonationHistoryContext"

interface DonationsListProps {
  donationYears: number[] | undefined
  activeYear: number
  activeFund: Fund | null
  hideNondeductibleDonations: boolean
  downloadUrl: string
}

export default function DonationsList({
  donationYears,
  activeYear,
  activeFund,
  hideNondeductibleDonations,
  downloadUrl,
}: DonationsListProps) {
  const { data, isError, fetchNextPage, hasNextPage, isFetching } =
    useSuspenseInfiniteQuery({
      queryKey: [
        "Donations",
        activeYear,
        activeFund || "allFunds",
        hideNondeductibleDonations,
      ],
      queryFn: ({ pageParam }) => {
        return fetchDonations({
          year: activeYear,
          fund: activeFund,
          hideNondeductibleDonations,
          pageParam,
        })
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.meta?.next?.offset,
    })
  const donations: Donation[] =
    data.pages.flatMap((page) => page.donations) || []
  const donationsMeta: DonationsResourceMeta | undefined = data.pages[0]?.meta
  const loadMore = () => !isFetching && fetchNextPage()

  const { showDownloadButton, setShowDownloadButton } =
    useDonationHistoryContext()

  const shouldDisplay = donations?.length > 0
  if (showDownloadButton !== shouldDisplay) {
    setShowDownloadButton(shouldDisplay)
  }

  return isError ? (
    <AlertMessage type="error" icon="general#exclamation-triangle">
      Something went wrong. Please try again.
    </AlertMessage>
  ) : (
    donations &&
      (donations.length === 0 ? (
        <BlankState
          donationType="monetary"
          activeYear={
            donationYears && donationYears.length > 1 ? activeYear : null
          }
          activeFund={activeFund}
        />
      ) : (
        <>
          <div className="d-f fd-c fd-r@sm g-4p g-1@sm ai-fs jc-sb mt-2">
            <div className="fw-600 tt-u">
              {donationsMeta.total_count}{" "}
              <span className="c-tint2">
                {pluralize(donationsMeta.active_total_count, "donation", false)}{" "}
                in{" "}
              </span>
              {activeYear} <span className="c-tint2">totaling</span>{" "}
              {formattedCents(donationsMeta.received_total_amount_cents)}{" "}
              {activeFund && (
                <>
                  <span className="c-tint2">to</span> {activeFund.name}
                </>
              )}
            </div>
            <a
              href={downloadUrl}
              css={css`
                [data-cca-platform] & {
                  display: inline-flex !important;
                }
              `}
              className="btn sm-btn outline-btn d-n d-if@sm"
            >
              Download
            </a>
          </div>
          <InfiniteScroll
            hasMore={hasNextPage}
            loadMore={loadMore}
            loader={<Loading key="loading" />}
          >
            {donations.map((donation) => (
              <DonationDetails key={donation.id} donation={donation} />
            ))}
          </InfiniteScroll>
        </>
      ))
  )
}
